import { Repository } from "react3l-common";
import { kebabCase } from "lodash";
import { httpConfig } from 'config/http';
import { BASE_API_URL } from "config/consts";
import { Observable } from "rxjs";
import { AxiosResponse } from "axios";

import nameof from "ts-nameof.macro";

import { API_POSITION_PREFIX } from "config/api-consts";
import { Position, PositionFilter } from 'models/Position';
import { Status, StatusFilter } from 'models/Status';

export type KeyType = string | number;

export class PositionRepository extends Repository {
    constructor() {
        super(httpConfig);
        this.baseURL = new URL(API_POSITION_PREFIX, BASE_API_URL).href;
    }

    public count = (positionFilter?: PositionFilter): Observable<number> => {
        return this.http.post<number>(kebabCase(nameof(this.count)), positionFilter)
            .pipe(Repository.responseDataMapper<number>());
    };

    public list = (positionFilter?: PositionFilter): Observable<Position[]> => {
        return this.http.post<Position[]>(kebabCase(nameof(this.list)), positionFilter)
            .pipe(Repository.responseMapToList<Position>(Position));
    };

    public get = (id: number | string): Observable<Position> => {
        return this.http.post<Position>
            (kebabCase(nameof(this.get)), { id })
            .pipe(Repository.responseMapToModel<Position>(Position));
    };

    public create = (position: Position): Observable<Position> => {
        return this.http.post<Position>(kebabCase(nameof(this.create)), position)
            .pipe(Repository.responseMapToModel<Position>(Position));
    };

    public update = (position: Position): Observable<Position> => {
        return this.http.post<Position>(kebabCase(nameof(this.update)), position)
            .pipe(Repository.responseMapToModel<Position>(Position));
    };

    public delete = (position: Position): Observable<Position> => {
        return this.http.post<Position>(kebabCase(nameof(this.delete)), position)
            .pipe(Repository.responseMapToModel<Position>(Position));
    };

    public bulkDelete = (idList: KeyType[]): Observable<void> => {
        return this.http.post(kebabCase(nameof(this.bulkDelete)), idList)
            .pipe(Repository.responseDataMapper());
    };

    public save = (position: Position): Observable<Position> => {
        return position.id ? this.update(position) : this.create(position);
    };

    public singleListStatus = (): Observable<Status[]> => {
        return this.http.post<Status[]>(kebabCase(nameof(this.singleListStatus)), new StatusFilter())
            .pipe(Repository.responseMapToList<Status>(Status));
    }
    public filterListStatus = (): Observable<Status[]> => {
        return this.http.post<Status[]>(kebabCase(nameof(this.filterListStatus)), new StatusFilter())
            .pipe(Repository.responseMapToList<Status>(Status));
    };

    public import = (file: File, name: string = nameof(file)): Observable<void> => {
        const formData: FormData = new FormData();
        formData.append(name, file as Blob);
        return this.http.post<void>(kebabCase(nameof(this.import)), formData)
            .pipe(Repository.responseDataMapper<any>());
    };

    public export = (filter: any): Observable<AxiosResponse<any>> => {
        return this.http.post('export', filter, {
            responseType: 'arraybuffer',
        });
    };

    public exportTemplate = (): Observable<AxiosResponse<any>> => {
        return this.http.post('export-template', {}, {
            responseType: 'arraybuffer',
        });
    };

}

export const positionRepository = new PositionRepository();
